export const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6Ldl4msrAAAAAD99uKwXNu8zwUk77_ZOVvZM2UOz';

/**
 * Dynamically loads the reCAPTCHA v3 script and ensures badge visibility.
 */
export const loadRecaptchaScript = () => {
  if (typeof window === 'undefined') return;

  // Make badge visible if previously hidden
  const badge = document.querySelector('.grecaptcha-badge');
  if (badge) {
    badge.style.visibility = 'visible';
    badge.style.display = 'block';
  }

  let script = document.getElementById('recaptcha-v3-script');
  if (!script) {
    script = document.createElement('script');
    script.id = 'recaptcha-v3-script';
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  }
};

/**
 * Hides the reCAPTCHA badge when unmounting from user form / reservation pages.
 */
export const hideRecaptchaBadge = () => {
  if (typeof window === 'undefined') return;
  const badge = document.querySelector('.grecaptcha-badge');
  if (badge) {
    badge.style.visibility = 'hidden';
    badge.style.display = 'none';
  }
};

/**
 * Promise-based loader and executor for reCAPTCHA v3 tokens.
 * Handles prefilled forms, uninitialized scripts, slow networks (~10s timeout), and ad blockers.
 *
 * @param {string} action - reCAPTCHA action tag (default: 'enqueue_user')
 * @param {number} retries - Number of retry attempts on network timeout
 * @returns {Promise<string>} - Generated reCAPTCHA token
 */
export const getRecaptchaToken = async (action = 'enqueue_user', retries = 1) => {
  const attemptTokenGen = () => {
    return Promise.race([
      new Promise((resolve, reject) => {
        if (typeof window === 'undefined') {
          return reject(new Error('Browser environment unavailable.'));
        }

        const executeGrecaptcha = () => {
          if (!window.grecaptcha || !window.grecaptcha.ready) {
            return reject(new Error('Security check script was blocked or failed to load. Please disable ad blockers and try again.'));
          }
          window.grecaptcha.ready(async () => {
            try {
              const token = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action });
              if (!token) {
                return reject(new Error('Failed to generate security token from Google.'));
              }
              resolve(token);
            } catch (err) {
              console.error('reCAPTCHA execution error:', err);
              reject(new Error('Security check execution failed. Please retry.'));
            }
          });
        };

        // If grecaptcha already exists on window
        if (window.grecaptcha && window.grecaptcha.ready) {
          return executeGrecaptcha();
        }

        // Check or inject script tag dynamically
        loadRecaptchaScript();

        // Poll for script initialization up to 8 seconds
        let attempts = 0;
        const checkInterval = setInterval(() => {
          attempts++;
          if (window.grecaptcha && window.grecaptcha.ready) {
            clearInterval(checkInterval);
            executeGrecaptcha();
          } else if (attempts >= 80) { // 8 seconds max polling (80 * 100ms)
            clearInterval(checkInterval);
            reject(new Error('Security check script initialization timed out. Please refresh and try again.'));
          }
        }, 100);
      }),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Security check timed out due to slow connection. Please try submitting again.')), 10000)
      ),
    ]);
  };

  try {
    return await attemptTokenGen();
  } catch (err) {
    if (retries > 0) {
      console.warn(`reCAPTCHA token generation failed, retrying... (${retries} retries left)`);
      return getRecaptchaToken(action, retries - 1);
    }
    throw err;
  }
};
